import { collapseDuplicateTrendData } from "./collapse-duplicate-rows";
import { LOINC_CODES } from "./constants";
import type { TrendData } from "./types";

/**
 * Trends biomarker summary counting — the single source of truth for the
 * "Biomarkers: N Total / Optimal / Normal / Out of range" card, shared by web
 * and mobile so the two surfaces (and iOS vs Android) can't drift.
 *
 * The count is ONE entry per biomarker (LOINC key), never one per data source.
 * Web previously counted source-expanded rows (clinic + wearable = 2), inflating
 * the total and double-bucketing status; mobile counted collapsed biomarkers.
 * This helper enforces the collapsed-biomarker unit for both.
 *
 * Range status per biomarker is decided by the injected `classify` — each surface
 * still passes its own classifier for now (grading-rule unification is a separate
 * follow-up). So this helper makes the TOTAL match across surfaces and de-dupes
 * the sub-buckets, without changing either surface's range-status semantics.
 */

/** Range status buckets shared by web and mobile Trends summaries. */
export type BiomarkerRangeStatus = "optimal" | "normal" | "out-of-range";

/** Summary tally shape for the Trends "Biomarkers" card. */
export interface BiomarkerSummaryCounts {
  total: number;
  optimal: number;
  normal: number;
  outOfRange: number;
  unknown: number;
}

/**
 * CGM / continuous-glucose biomarkers are excluded from the discrete biomarker
 * count — they render as a dedicated continuous card, not a table row. Kept
 * byte-identical to mobile's historical `isCGMBiomarker` so web and mobile drop
 * exactly the same keys; do not "improve" it (e.g. to unwrap split keys) without
 * changing both surfaces together, or the counts re-diverge.
 */
export function isCgmBiomarker(loincCode: string): boolean {
  return loincCode === LOINC_CODES.CGM_GLUCOSE || loincCode.startsWith("vibrant.cgm");
}

/**
 * Tally the Trends summary counts from an overview payload, counting one entry
 * per biomarker. Drops biomarkers with no observations in the window and CGM
 * biomarkers. `classify` returning anything other than a known status (including
 * the web classifier's `"unknown"` or a mobile `undefined`) buckets as `unknown`.
 *
 * Pure and platform-agnostic: identical input → identical output. That property
 * is what guarantees the iOS and Android counts are the same.
 */
export function countBiomarkers(
  data: Record<string, TrendData> | null | undefined,
  classify: (
    loincCode: string,
    trendData: TrendData,
  ) => BiomarkerRangeStatus | "unknown" | undefined,
): BiomarkerSummaryCounts {
  const counts: BiomarkerSummaryCounts = {
    total: 0,
    optimal: 0,
    normal: 0,
    outOfRange: 0,
    unknown: 0,
  };
  if (!data) {
    return counts;
  }

  // Collapse the same patient-indistinguishable duplicates the list builders do
  // (getExpandedTrendRows / buildBiomarkers), so the "N Total" count matches the
  // rendered row count. Both callers are patient surfaces; the count/list-agreement
  // invariant breaks if only one side collapses.
  for (const [loincCode, trendData] of Object.entries(collapseDuplicateTrendData(data))) {
    if (trendData.observations.length === 0) continue; // no data in the window
    if (isCgmBiomarker(loincCode)) continue; // continuous glucose shown separately

    counts.total++;
    const status = classify(loincCode, trendData);
    if (status === "optimal") counts.optimal++;
    else if (status === "normal") counts.normal++;
    else if (status === "out-of-range") counts.outOfRange++;
    else counts.unknown++;
  }

  return counts;
}
